// Entry: global styles, dev overrides, mock backend, native bridge, session, router, render.
import { render } from 'preact';
import './styles/global.css';
import { App } from './app';
import { installMock, mockEnabled } from './api/mock';
import { initNative, hideSplash, onNative } from './native';
import { initSession, bootSession, appStore } from './store';
import { initRouter, isOnboarding, openRoute, replace, reset, routerStore, setHomeResolver } from './router';
import { homeScreen } from './home';
import * as store from './store';
import * as router from './router';
import { api } from './api/client';
import { draftStore } from './screens/onboarding/draft';
import { installErrorReporting, installFeedback } from './feedback';
import * as analytics from './lib/analytics';

// Page errors from the very start (feedback/errors.ts): remembered for the β sheet and reported to the server.
installErrorReporting();
const gallery = new URLSearchParams(location.search).get('gallery') === '1';

// Dev/screenshot overrides: ?inset=47,34 simulates safe areas (top,bottom px); ?motion=reduce forces reduced motion.
try {
  const q = new URLSearchParams(location.search);
  const inset = q.get('inset');
  if (inset) {
    const [t, b] = inset.split(',').map((x) => Math.max(0, Number(x) || 0));
    document.documentElement.style.setProperty('--xm-sat', t + 'px');
    document.documentElement.style.setProperty('--xm-sab', (b || 0) + 'px');
  }
  if (q.get('motion') === 'reduce') document.documentElement.dataset.reducedMotion = 'true';
} catch { /* ignore */ }

if (mockEnabled()) {
  installMock();
  // Debug handle for the console and tests (mock mode only): xm.store.doneCard('r'), xm.router.go('U1'), ...
  (window as unknown as { xm: unknown }).xm = { store, router, api, draft: draftStore, analytics };
}
// Admin events (lib/analytics.ts): visits, notification opens, flushes. Before initNative(), like the cold-start tap
// below, so a tap that launched the app counts as the reason it opened.
if (!gallery) analytics.installAnalytics();
// A notification tap can cold-start the app: its route arrives before the session is back. Keep the last one and open it
// once boot is done, on top of home, so boot never replaces it. (After boot, app.tsx handles routes.) Subscribed before
// initNative() so no early event is missed.
let tapped: string | null = null;
let bootDone = false;
onNative((e) => { if (e.type === 'route' && !bootDone) tapped = e.route; });
initNative();
initSession();
const restored = initRouter();
setHomeResolver(homeScreen);

const root = document.getElementById('app')!;
if (gallery) {
  // Component gallery (dev reference, screenshotted by scripts/shots.mjs). Loaded on demand only.
  void import('./dev/Gallery').then(({ Gallery }) => render(<Gallery />, root));
} else {
  render(<App />, root);
  // Internal beta: the β button, the trail of recent steps and reports that wait for a connection (feedback/).
  installFeedback();
}

void bootSession().then((state) => {
  bootDone = true;
  const cur = routerStore.get().s;
  if (tapped && appStore.get().token) {
    reset(homeScreen());
    openRoute(tapped);
  } else if (state) {
    if (!restored || isOnboarding(cur)) replace(homeScreen());
  } else if (!restored || !isOnboarding(cur)) {
    replace(homeScreen());
  }
  requestAnimationFrame(() => hideSplash());
});
